import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { User, Shield, AlertTriangle, MapPin, Calendar, Accessibility, MessageCircle, Camera, HelpCircle, RefreshCw, Pencil, StickyNote, Clock } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { SupportSnapshotEditDrawer } from './SupportSnapshotEditDrawer';
import { SupportSnapshotGoals } from './SupportSnapshotGoals';

const SEVERITY_STYLES = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-slate-100 text-slate-600 border-slate-200',
};

const REVIEW_STATUS_STYLES = {
  current: 'bg-emerald-50 text-emerald-600',
  due_soon: 'bg-amber-50 text-amber-600',
  overdue: 'bg-red-50 text-red-600',
  stale: 'bg-orange-50 text-orange-600',
  inactive: 'bg-slate-100 text-slate-400',
};

const FRESHNESS_STYLES = {
  fresh: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  review_soon: 'bg-sky-50 text-sky-700 border-sky-200',
  stale: 'bg-amber-50 text-amber-700 border-amber-200',
  outdated: 'bg-red-50 text-red-700 border-red-200',
  never_reviewed: 'bg-slate-100 text-slate-500 border-slate-200',
};

const FRESHNESS_LABELS = {
  fresh: 'Fresh', review_soon: 'Review Soon', stale: 'Stale', outdated: 'Outdated', never_reviewed: 'Never Reviewed',
};

const SUPPORT_LEVEL_STYLES = {
  independent: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  semi: 'bg-sky-50 text-sky-700 border-sky-200',
  full: 'bg-violet-50 text-violet-700 border-violet-200',
};

const PHOTO_STATUS_STYLES = {
  compliant: 'bg-emerald-50 text-emerald-700',
  missing: 'bg-red-50 text-red-700',
  due_soon: 'bg-amber-50 text-amber-700',
  overdue: 'bg-red-50 text-red-700',
};

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function timeAgo(iso) {
  if (!iso) return 'Never';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return formatDateTime(iso);
  } catch { return iso; }
}

export function SupportSnapshotCard({ participantId, editFlagEnabled, intelligenceEnabled }) {
  const { user } = useAuth();
  const canManageSnapshot = (user?.permissions || []).includes('participants.manage_support_snapshot');
  const showEditButton = editFlagEnabled && canManageSnapshot;

  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const fetchSnapshot = useCallback(() => {
    if (!participantId) return;
    setLoading(true);
    setError(false);
    api.get(`/participants/${participantId}/support-snapshot`)
      .then(r => setSnapshot(r.data.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [participantId]);

  useEffect(() => { fetchSnapshot(); }, [fetchSnapshot]);

  const handleSaved = () => { setEditOpen(false); fetchSnapshot(); };

  if (loading) {
    return (
      <Card className="border-dashed" data-testid="snapshot-loading">
        <CardContent className="py-4 flex items-center gap-2 justify-center">
          <RefreshCw className="w-4 h-4 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Loading support snapshot...</span>
        </CardContent>
      </Card>
    );
  }

  if (error || !snapshot) {
    return (
      <Card className="border-dashed" data-testid="snapshot-empty">
        <CardContent className="py-4 text-center">
          <p className="text-xs text-muted-foreground">No support snapshot available</p>
          {showEditButton && (
            <Button variant="outline" size="sm" className="mt-2" onClick={() => { setSnapshot({}); setEditOpen(true); }} data-testid="snapshot-create-btn">
              <Pencil className="w-3 h-3 mr-1" /> Create Snapshot
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  const { mobility, communication, alerts, assistance_required, address_summary, next_schedule, photo_compliance, primary_disability, support_level, quick_notes, meta, care_plan_goals_summary } = snapshot;
  const freshness = meta?.freshness_status;

  return (
    <>
      <Card className="border-l-4 border-l-primary/30" data-testid="support-snapshot-card">
        <CardContent className="py-4 space-y-3">

          {/* Header Row: Badges + Meta + Edit */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 space-y-1.5">
              {/* Badges Row */}
              <div className="flex items-center gap-2 flex-wrap" data-testid="snapshot-badges">
                {primary_disability && (
                  <Badge variant="outline" className="text-[10px] gap-1"><Shield className="w-3 h-3" /> {primary_disability}</Badge>
                )}
                {support_level && (
                  <Badge className={`text-[10px] ${SUPPORT_LEVEL_STYLES[support_level] || 'bg-muted'}`}>
                    {support_level === 'full' ? 'Full Support' : support_level === 'semi' ? 'Semi-Independent' : 'Independent'}
                  </Badge>
                )}
                <Badge variant="outline" className="text-[10px] gap-1"><MessageCircle className="w-3 h-3" /> {communication?.type || 'verbal'}</Badge>
                {mobility?.wheelchair && <Badge variant="outline" className="text-[10px] gap-1"><Accessibility className="w-3 h-3" /> Wheelchair</Badge>}
                {mobility?.transfer_required && <Badge variant="outline" className="text-[10px] gap-1"><HelpCircle className="w-3 h-3" /> Transfer Required</Badge>}
                {alerts?.length > 0 && (
                  <Badge variant="destructive" className="text-[10px] gap-1" data-testid="snapshot-alert-count">
                    <AlertTriangle className="w-3 h-3" /> {alerts.length} Alert{alerts.length > 1 ? 's' : ''}
                  </Badge>
                )}
                {photo_compliance && (
                  <Badge className={`text-[10px] gap-1 ${PHOTO_STATUS_STYLES[photo_compliance.status] || ''}`}>
                    <Camera className="w-3 h-3" /> Photo: {photo_compliance.status}
                  </Badge>
                )}
              </div>

              {/* Meta: Updated by + Freshness (Day 3) */}
              {intelligenceEnabled && meta && (
                <div className="flex items-center gap-2 flex-wrap" data-testid="snapshot-meta-row">
                  <span className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid="snapshot-updated-by">
                    <Clock className="w-3 h-3" />
                    {meta.updated_at ? `Updated ${timeAgo(meta.updated_at)}` : 'Never reviewed'}
                    {meta.updated_by_name && <span> by {meta.updated_by_name}</span>}
                  </span>
                  {freshness && (
                    <Badge className={`text-[9px] ${FRESHNESS_STYLES[freshness] || ''}`} data-testid="snapshot-freshness-badge">
                      {FRESHNESS_LABELS[freshness] || freshness}
                    </Badge>
                  )}
                </div>
              )}
            </div>

            {showEditButton && (
              <Button variant="outline" size="sm" className="h-7 text-xs flex-shrink-0" onClick={() => setEditOpen(true)} data-testid="snapshot-edit-btn">
                <Pencil className="w-3 h-3 mr-1" /> Edit
              </Button>
            )}
          </div>

          {/* Quick Notes Banner */}
          {quick_notes && (
            <div className="flex items-start gap-2 px-3 py-2 bg-amber-50/50 border border-amber-200/50 rounded text-xs" data-testid="snapshot-quick-notes">
              <StickyNote className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
              <span>{quick_notes}</span>
            </div>
          )}

          {/* Content Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

            {/* Alerts Section */}
            <div data-testid="snapshot-alerts-section">
              <p className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Alerts
              </p>
              {alerts?.length > 0 ? (
                <div className="space-y-1">
                  {alerts.slice(0, 5).map((a, i) => (
                    <div key={a.id || i} className={`text-xs px-2 py-1.5 rounded border ${SEVERITY_STYLES[a.severity] || SEVERITY_STYLES.low}`} data-testid={`snapshot-alert-${i}`}>
                      <div className="flex items-center justify-between gap-1">
                        <span className="font-medium">{a.type}</span>
                        <div className="flex items-center gap-1">
                          <Badge className={`text-[8px] px-1 py-0 ${SEVERITY_STYLES[a.severity] || ''}`}>{a.severity}</Badge>
                          {intelligenceEnabled && a.review_status && (
                            <Badge className={`text-[8px] px-1 py-0 ${REVIEW_STATUS_STYLES[a.review_status] || ''}`} data-testid={`alert-review-status-${i}`}>
                              {a.review_status === 'due_soon' ? 'Due Soon' : a.review_status}
                            </Badge>
                          )}
                        </div>
                      </div>
                      {a.notes && <p className="text-[10px] opacity-70 mt-0.5">{a.notes}</p>}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic">No active alerts</p>
              )}
            </div>

            {/* Assistance Required */}
            <div data-testid="snapshot-assistance-section">
              <p className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1">
                <User className="w-3 h-3" /> Assistance Required
              </p>
              {assistance_required?.length > 0 ? (
                <div className="space-y-1">
                  {assistance_required.slice(0, 5).map((a, i) => (
                    <div key={i} className="text-xs flex items-center justify-between px-2 py-1 rounded bg-muted/30" data-testid={`snapshot-assist-${i}`}>
                      <span className="capitalize">{a.category}</span>
                      <Badge variant="secondary" className="text-[9px]">{a.level?.replace(/_/g, ' ')}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic">No assistance needs recorded</p>
              )}
            </div>

            {/* Address + Schedule + Notes */}
            <div className="space-y-3">
              <div data-testid="snapshot-address">
                <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> Address</p>
                <p className="text-xs">{address_summary || 'Not recorded'}</p>
              </div>
              <div data-testid="snapshot-schedule">
                <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> Next Schedule</p>
                {next_schedule ? (
                  <p className="text-xs"><Badge variant="outline" className="text-[9px] mr-1">{next_schedule.type}</Badge>{formatDateTime(next_schedule.datetime)}</p>
                ) : (
                  <p className="text-xs text-muted-foreground italic">No upcoming schedule</p>
                )}
              </div>
              {communication?.notes && (
                <div data-testid="snapshot-comm-notes">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Communication Notes</p>
                  <p className="text-xs">{communication.notes}</p>
                </div>
              )}
              {mobility?.notes && (
                <div data-testid="snapshot-mobility-notes">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Mobility Notes</p>
                  <p className="text-xs">{mobility.notes}</p>
                </div>
              )}
            </div>
          </div>

          {/* Care Plan Goals Summary (Day 4) */}
          {care_plan_goals_summary && (
            <SupportSnapshotGoals goals={care_plan_goals_summary} participantId={participantId} />
          )}
        </CardContent>
      </Card>

      {editOpen && (
        <SupportSnapshotEditDrawer participantId={participantId} snapshot={snapshot} open={editOpen} onClose={() => setEditOpen(false)} onSaved={handleSaved} />
      )}
    </>
  );
}
